import {
	type SDK_Filter,
	type SDK_Fonts,
	type SDK_TableCell,
	strings,
} from "@quickpoint.me/shared";
import { createContext } from "react";
import { useRecoilValue } from "recoil";
import styled, { ThemeProvider } from "styled-components";
import type { GridObject } from "../../lib/calculateGridRowsAndColumns";
import { startingPoint } from "../../lib/cssHelpers";
import { mainCssRules } from "../../lib/cssTemplates";
import { FilterFonts, FilterName, FilterSelector } from "../../state/filter";
import {
	PageElementsFamily,
	PageIndex,
	PageSizeAtom,
	createLocation,
} from "../../state/page";
import { ConstrainDimensionsSelector } from "../../state/ui";

interface FigureOutSpansArgs {
	currentCol: number;
	isInLandscape: boolean;
	isLastColInRow: boolean;
	maxCols: number;
}

export const IsInFlexBoxContext = createContext(false);

function figureOutSpans({
	currentCol,
	isInLandscape,
	isLastColInRow,
	maxCols,
}: FigureOutSpansArgs) {
	if (isLastColInRow && maxCols > currentCol && isInLandscape) {
		return `${currentCol}  /  ${maxCols + 1}`;
	}
	return currentCol;
}

const StyledCell = styled.div<{
	$alignCellAsColumn: boolean;
	$baseFontSize: number;
	$baseFontSizeRaw: number;
	$column: number;
	$filter: SDK_TableCell;
	$fonts: SDK_Fonts;
	$inEditMode: boolean;
	$constrainDimensions: boolean;
	$isInLandscape: boolean;
	$isPreviewer: boolean;
	$isSplitPageChildCell?: boolean;
	$isSplitCell?: boolean;
	$lastColInRow: boolean;
	$lastRowInGrid: boolean;
	$maxCols: number;
	$maxRows: number;
	$row: number;
	$isSpacer?: boolean;
	$forceSingleColumn: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: ${(props) => props.$forceSingleColumn ? "flex-start" : startingPoint(props.theme.startingPoint)} ;
  position: relative;
  min-height: 0;
  max-height: 100%;
  grid-column: ${(props) => {
		if (props.$isSplitCell) return "1 / -1";
		const args = {
			currentCol: props.$column,
			isInLandscape: props.$isInLandscape,
			isLastColInRow: props.$lastColInRow,
			maxCols: props.$maxCols,
		};
		return figureOutSpans(args);
	}};
  grid-row: ${(props) => props.$row};
  height: ${(props) => (props.$isSpacer ? "1em" : "auto")};
  width: ${(props) => (props.$isSplitPageChildCell && props.$isInLandscape ? "90%" : "100%")};
  --cell-background: "unset";
  --cell-background-repeat: no-repeat;
  --cell-background-position: center;
  --cell-background-size: cover;
  --cell-opacity: 1;
  --cell-blur: 0;
  &::before {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    visibility: visible;
    background: var(--cell-background);
    background-repeat: var(--cell-background-repeat);
    background-position: var(--cell-background-position);
    background-size: var(--cell-background-size);
    filter: blur(var(--cell-blur)) opacity(var(--cell-opacity));
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    max-width: 100%;
    max-height: 100%;
  }
  ${(props) =>
		`${mainCssRules({
			baseFontSize: props.$baseFontSize,
			baseFontSizeRaw: props.$baseFontSizeRaw,
			fonts: props.$fonts,
			theme: props.$filter,
		})}`};
  ${(props) => (props.$isSplitPageChildCell ? "padding: 0;" : "")}
  ${(props) => (props.$isSplitPageChildCell ? "justify-self: center;" : "")}
  ${(props) => (props.$isInLandscape ? "" : "padding-inline: 0;")}
 
`;

export const IsInCellContext = createContext(true);

function TableCell({
	gridObject,
	idx,
	inEditMode,
	isPreviewer,
	alignCellAsColumn,
	isSplitPageChildCell,
}: {
	alignCellAsColumn: boolean;
	gridObject: GridObject;
	idx: number;
	inEditMode: boolean;
	isPreviewer: boolean;
	isSplitPageChildCell?: boolean;
}) {
	const filterName = useRecoilValue(FilterName);
	const filter = useRecoilValue(FilterSelector(filterName));
	const fonts = useRecoilValue(FilterFonts);
	const pageSize = useRecoilValue(PageSizeAtom);
	const constrainDimensions = useRecoilValue(ConstrainDimensionsSelector);
	const isInLandscape = pageSize.landscape;
	const baseFontSize = pageSize.baseFontSize;
	const pageIndex = useRecoilValue(PageIndex);

	const titleAtomId = gridObject.columnTitleAtomId;
	let label: string | undefined;

	/**
	 * A theme function that intercepts the parent theme. If the grid is a responsive table,
	 * it hijacks the theme and replaces the 'paragraph' styles with the 'table' styles.
	 * This allows descendant components (like Paragraph) to automatically use table typography
	 * without needing to be aware of the table context.
	 * @param {object} outerTheme - The theme from the parent ThemeProvider.
	 * @returns {object} The modified or original theme.
	 */
	const tableAwareTheme = (outerTheme: SDK_Filter) => {
		return {
			...outerTheme,
			paragraph: outerTheme.table,
		};
	};

	if (titleAtomId !== undefined) {
		const titleElementData = useRecoilValue(
			PageElementsFamily(createLocation(pageIndex, titleAtomId)),
		);
		if (typeof titleElementData?.data?.content === "string") {
			label = titleElementData.data.content;
		}
	}

	// header cells (the columnTitle: row) must expose columnheader role or
	// assistive tech reads the table without any header/cell association
	const isHeaderCell = gridObject.elArray.some(
		(el) => el.props?.type === "columnTitle:",
	);
	return (
		<ThemeProvider theme={tableAwareTheme}>
			<IsInFlexBoxContext.Provider value={true}>
				<IsInCellContext.Provider value={true}>
					<StyledCell
						$alignCellAsColumn={alignCellAsColumn}
						$baseFontSize={baseFontSize}
						$baseFontSizeRaw={pageSize.baseFontSizeRaw}
						$column={gridObject.col}
						$filter={filter.tableCell}
						$fonts={fonts}
						$inEditMode={inEditMode}
						$constrainDimensions={constrainDimensions}
						$isInLandscape={isInLandscape}
						$isPreviewer={isPreviewer}
						$isSplitPageChildCell={isSplitPageChildCell}
						$isSplitCell={gridObject.isSplitCell}
						$lastColInRow={gridObject.lastColInRow}
						$lastRowInGrid={gridObject.lastRowInGrid}
						$maxCols={gridObject.maxCols}
						$maxRows={gridObject.maxRows}
						$row={gridObject.row}
						$isSpacer={gridObject.isSpacer}
						$forceSingleColumn={pageSize.forceSingleColumn}
						className={`${strings.css.styledGridCell} `}
						id={`grid-cell${idx}-col-${gridObject.col}-row-${gridObject.row}`}
						key={`grid-cell${idx}-col-${gridObject.col}-row-${gridObject.row}-${pageIndex}`}
						// biome-ignore lint/a11y/useSemanticElements: <explanation>
						role={isHeaderCell ? "columnheader" : "cell"}
						data-label={label}
					>
						{gridObject.elArray}
					</StyledCell>
				</IsInCellContext.Provider>
			</IsInFlexBoxContext.Provider>
		</ThemeProvider>
	);
}
export default TableCell;
